import {
  hashKey,
  notifyManager,
  QueryCacheNotifyEvent,
  QueryKey,
  useQueryClient
} from '@tanstack/react-query';
import {
  AsyncState,
  AsyncStateProps,
  AsyncVisualState
} from '@unistocks/components/query-state/async-state';
import { getCacheSignature, getQueryStates } from '@unistocks/components/query-state/utils';
import { MaybePromise } from '@unistocks/types';
import { FC, useSyncExternalStore } from 'react';

export type QueryStateProps = Omit<AsyncStateProps, 'state' | 'error' | 'onRetry'> & {
  queryKeys: readonly QueryKey[];
  /**
   * When `true`, the queries have successfully resolved but returned no data, so the `noDataText`/
   * `noDataIcon` "no data" state is rendered instead of the children. Only applies once the queries
   * are neither loading nor errored.
   */
  isEmpty?: boolean;
  /**
   * A callback function that is called when the user clicks the retry button in the error state.
   * If not provided, the component will automatically refetch all queries in `queryKeys` when the retry button is clicked.
   * This is useful if you want to perform additional actions when retrying, such as resetting local state or logging.
   * If you provide this prop, you are responsible for refetching the queries yourself.
   * The function can return a promise if the retry action is asynchronous.
   * If you want to use the default behavior of refetching all queries, simply omit this prop.
   */
  onRetry?: () => MaybePromise<void>;
};

/**
 * A component that monitors the state of multiple queries and displays the appropriate UI state (loading, error, or no data) based on their combined status.
 */
export const QueryState: FC<QueryStateProps> = ({ queryKeys, onRetry, isEmpty, ...props }) => {
  const queryClient = useQueryClient();
  const queryCache = queryClient.getQueryCache();
  const queryStates = getQueryStates(queryClient, queryKeys);

  useSyncExternalStore(
    (onStoreChange: () => void) => {
      const keyHashes = new Set(queryKeys.map(queryKey => hashKey(queryKey)));

      return queryCache.subscribe(
        notifyManager.batchCalls(({ query }: QueryCacheNotifyEvent) => {
          if (query && keyHashes.has(query.queryHash)) {
            onStoreChange();
          }
        })
      );
    },
    () => getCacheSignature(queryClient, queryKeys),
    () => getCacheSignature(queryClient, queryKeys)
  );

  const isLoadingAny = queryStates.some(queryState => queryState.status === 'pending');
  const isErrorAny = queryStates.some(queryState => queryState.status === 'error');
  const isFetchingAny = queryStates.some(queryState => queryState.fetchStatus === 'fetching');
  const error = queryStates.find(queryState => queryState.status === 'error')?.error;

  // Loading while waiting for initial data or retrying an error state, but not when refetching in the background
  const state: AsyncVisualState =
    isLoadingAny || (isErrorAny && isFetchingAny)
      ? 'loading'
      : isErrorAny
        ? 'error'
        : isEmpty
          ? 'empty'
          : null;

  const retry = async () => {
    if (onRetry) {
      return onRetry();
    }

    await Promise.all(
      queryKeys.map(queryKey => queryClient.refetchQueries({ queryKey, exact: true }))
    );
  };

  return <AsyncState state={state} error={error} onRetry={retry} {...props} />;
};

QueryState.displayName = 'QueryState';
